import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import {
  SEO,
  Content,
  Text,
  Spacing,
  devices,
  Stack,
  WhiteButton,
  BlueButtonBasic,
} from '../components/common';
import Article from '../components/Article';
import { Service, Employee } from '../@types/types';
import HeroSection from '../components/HeroSection';
import { ExternalLink } from '../components/SVG';
import Modal from '../components/Modal';
import Markdown from '../components/Markdown';
import type { Color } from '../theme';
import theme from '../theme';

type Props = {
  data: { service: Service };
  location: Location;
};

const ServicePage = ({ data, location }: Props) => {
  const {
    service: {
      Name,
      PageTopSection,
      SubTitle,
      DescriptionDetails,
      EmployeeTitle,
      employees,
    },
  } = data;

  const priorityEmployees = employees.filter(
    (employee) => employee.priorityEmployee === true
  );

  const nonPriorityEmployees = employees.filter(
    (employee) =>
      employee.priorityEmployee === false || employee.priorityEmployee === null
  );

  const seoDescription = (
    PageTopSection?.TopSectionText ||
    SubTitle ||
    ''
  ).substring(0, 270);

  const [showModal, setShowModal] = useState(false);
  const [employeeData, setEmployeeData] = useState(false);
  const [randomPriorityData, setRandomPriorityData] = useState<any[]>([]);
  const [randomNonPriorityData, setRandomNonPriorityData] = useState<any[]>([]);

  const openModal = (employee: any) => {
    setShowModal((showModal) => !showModal);
    setEmployeeData(employee);
  };

  const shuffleEmployee = (array: any[]) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  useEffect(() => {
    setRandomPriorityData(shuffleEmployee(priorityEmployees));
    setRandomNonPriorityData(shuffleEmployee(nonPriorityEmployees));
  }, []);

  return (
    <>
      <SEO
        pathname={location.pathname}
        title={Name}
        description={seoDescription}
      />
      <Modal
        showModal={showModal}
        setShowModal={setShowModal}
        employeeData={employeeData}
      />

      {PageTopSection && <HeroSection topSection={PageTopSection} />}
      <Article title={SubTitle} contentData={DescriptionDetails} />
      {employees.length !== 0 ? (
        <Container bgColor={'white' || 'lightBlue'}>
          <Content>
            <Title variant="title-2">
              {EmployeeTitle || 'Asiantuntijamme'}
            </Title>
          </Content>
          <Spacing axis="y" amount="xxlarge" />
          <EmployeeContent axis="x">
            {[...randomPriorityData, ...randomNonPriorityData].map(
              (employee: Employee, index: number) => {
                return (
                  <ProfileContainer key={index}>
                    <ContentContainer>
                      <Image src={employee.Picture.publicURL} />
                      <ProfileDetails>
                        <NameAndTitle>
                          <NameText variant="subtitle">
                            {employee.Name}
                          </NameText>
                          <TitleText variant="body">{employee.Title}</TitleText>
                        </NameAndTitle>

                        {employee.Description && (
                          <DescriptionSection>
                            {employee.Description}
                          </DescriptionSection>
                        )}

                        <ExternalLinks>
                          {employee.ReservationLink && (
                            <Button href={employee.ReservationLink}>
                              Varaa aika
                              <CustomIcon fill={theme.colors.white} />
                            </Button>
                          )}

                          <ModalButton
                            onClick={() => openModal(employee)}
                            linkExist={employee.ReservationLink}
                          >
                            Lisätietoa
                          </ModalButton>
                        </ExternalLinks>
                      </ProfileDetails>
                    </ContentContainer>
                  </ProfileContainer>
                );
              }
            )}
          </EmployeeContent>
        </Container>
      ) : null}
    </>
  );
};

export default ServicePage;

export const query = graphql`
  query ServiceQuery($slug: String!) {
    service: strapiServices(slug: { eq: $slug }) {
      ...strapiServicesFragment
    }
  }
`;

const Container = styled.div<{ bgColor: Color }>`
  background-color: ${(props) => props.theme.colors[props.bgColor]};
  padding: 100px 0px;
`;

const Title = styled(Text)`
  @media ${devices.tablet} {
    text-align: center;
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 30%;
  align-items: center;
  padding-bottom: 80px;

  @media ${devices.tablet} {
    flex: 0 0 50%;

    &:last-child {
      padding-bottom: 0px;
    }
  }
`;

const ContentContainer = styled.div`
  width: 300px;

  @media ${devices.tablet} {
    width: 100%;
  }
`;

const Image = styled.img`
  border-radius: 50%;
  height: 300px;
  width: 300px;
  margin: auto;

  @media ${devices.mobileS} {
    height: 200px;
    width: 200px;
    display: block;
  }
`;

const ProfileDetails = styled(Stack)`
  display: flex;
  flex-direction: column;
  margin-top: ${(props) => props.theme.spacing.medium};
  text-align: center;

  @media ${devices.mobileXL} {
    margin: auto;
  }
`;

const NameText = styled(Text)`
  font-size: ${(props) => props.theme.sizing.medium};

  @media ${devices.tablet} {
    text-align: center;
  }
`;

const TitleText = styled(Text)`
  font-style: italic;
  font-size: calc((${(props) => props.theme.sizing.default}) + 1px);

  @media ${devices.tablet} {
    text-align: center;
  }
`;

const DescriptionSection = styled(Markdown)`
  line-height: 20px;

  @media ${devices.tablet} {
    text-align: center;
  }
`;

const ExternalLinks = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  @media ${devices.tablet} {
    flex-direction: column;
  }
`;

const CustomIcon = styled(ExternalLink)`
  margin-left: 10px;
`;
const NameAndTitle = styled.div`
  @media ${devices.mobileXL} {
    margin-top: ${(props) => props.theme.sizing.default};
  }
`;

const Button = styled(BlueButtonBasic)`
  @media ${devices.tablet} {
    width: 80%;
    max-width: 300px;
    margin: ${(props) => props.theme.sizing.default} 0px;
    justify-content: space-between;
  }
`;

const ModalButton = styled(WhiteButton)<{ linkExist: string }>`
  cursor: pointer;
  margin: ${(props) => !props.linkExist && 'auto'};

  @media ${devices.tablet} {
    width: 80%;
    max-width: 300px;
  }
`;

const EmployeeContent = styled(Content)`
  flex-wrap: wrap;
  justify-content: space-between;

  @media ${devices.tablet} {
    flex-direction: column;
    width: 80%;
    max-width: 300px;
  }

  @media ${devices.mobileXL} {
    flex-direction: column;
  }

  @media ${devices.mobileS} {
    margin: 0px;
  }
`;
